import { apiService } from '../../../shared/services/apiServices';
import { AxiosError } from 'axios';
import { SUBSCRIPTION_ERROR_MESSAGES } from '../errors';

// ============================================================================
// API Response Types
// ============================================================================

export type SubscriptionPlanStatus = 'active' | 'inactive';
export type DurationType = 'MONTHLY' | 'YEARLY';
export type BillingCycle = 'MONTHLY' | 'QUARTERLY' | 'YEARLY';

export interface SubscriptionPlanLimits {
  numberOfUsers?: number;
  numberOfFacilities?: number;
  numberOfQmsDocuments?: number;
}

export interface SubscriptionOffer {
  offerId: string | null;
  offerName: string | null;
  discountValue: number | null;
}

export interface SubscriptionUserSummary {
  userId: string;
  name: string;
  profileUrl: string | null;
}

export interface SubscriptionServiceConfig {
  id: string;
  serviceTypeId: string;
  serviceName: string | null;
  // Only present when a discount is applied — see PlanPreviewAccordion's
  // Services section, which shows a dash otherwise.
  usageLimit: number | null;
  basePrice: number;
  discountPercentage: number | null;
}

export interface SubscriptionEquipmentConfig {
  id: string;
  equipmentCategoryId: string;
  equipmentCategoryName: string | null;
  basePrice: number;
}

export interface SubscriptionDiscountTier {
  id: string;
  equipmentRank: number;
  discountPercentage: number;
}

// Fields returned by both GET /subscriptions (list) and GET /subscriptions/{id}
export interface SubscriptionPlanBase {
  id: string;
  planName: string;
  planDescription: string;
  durationType: DurationType;
  duration: number;
  billingCycle: BillingCycle;
  price: number;
  limits: SubscriptionPlanLimits | null;
  status: SubscriptionPlanStatus;
  offer: SubscriptionOffer;
  activeClientCount: number;
  createdAt: string;
  updatedAt: string;
  createdBy: SubscriptionUserSummary | null;
  updatedBy: SubscriptionUserSummary | null;
}

// GET /subscriptions/{id} adds the plan's full configuration
export interface SubscriptionPlan extends SubscriptionPlanBase {
  services: SubscriptionServiceConfig[];
  equipmentCategories: SubscriptionEquipmentConfig[];
  discountTiers: SubscriptionDiscountTier[];
}

export interface ViewSubscriptionPlanApiResponse {
  success: boolean;
  data: SubscriptionPlan;
}

export interface ViewSubscriptionPlanResult {
  success: boolean;
  data?: SubscriptionPlan;
  error?: string;
  forbidden?: boolean;
}

export interface ViewSubscriptionPlanOptions {
  planId: string;
}

// ============================================================================
// Error Messages
// ============================================================================

export const ERROR_MESSAGES = {
  DEFAULT_ERROR: 'Wystąpił błąd podczas pobierania danych planu subskrypcji',
  NETWORK_ERROR: SUBSCRIPTION_ERROR_MESSAGES.NETWORK_ERROR,
  UNEXPECTED_ERROR: SUBSCRIPTION_ERROR_MESSAGES.UNEXPECTED_ERROR,
} as const;

const extractErrorMessage = (errorData: unknown): string => {
  if (!errorData) {
    return ERROR_MESSAGES.DEFAULT_ERROR;
  }

  if (typeof errorData === 'string') {
    return errorData;
  }

  if (typeof errorData === 'object') {
    const apiError = errorData as { message?: string; error?: string };
    const errorMessage = apiError.message || apiError.error;

    if (errorMessage) {
      return errorMessage;
    }

    return ERROR_MESSAGES.DEFAULT_ERROR;
  }

  return ERROR_MESSAGES.DEFAULT_ERROR;
};

/**
 * Fetches subscription plan details by planId (UUID).
 * Endpoint: GET loopay-subscription-management/subscriptions/{planId}
 */
export const viewSubscriptionPlan = async (
  options: ViewSubscriptionPlanOptions
): Promise<ViewSubscriptionPlanResult> => {
  const { planId } = options;

  if (!planId?.trim()) {
    return {
      success: false,
      error: ERROR_MESSAGES.DEFAULT_ERROR,
    };
  }

  try {
    const result = await apiService.getAPI(
      `loopay-subscription-management/subscriptions/${planId.trim()}`,
      { skipGlobalErrorRedirect: true }
    );

    if (result.error && result.status === 403) {
      return { success: false, forbidden: true };
    }

    if (!result.error) {
      // API returns { success: true, data: {...} }
      const response = result.data as ViewSubscriptionPlanApiResponse;
      const planData = (response.data || result.data) as SubscriptionPlan;

      return {
        success: true,
        data: planData,
      };
    } else {
      const errorData = result.data;
      const isNetworkError = result.isNetworkError;

      const errorMessage = extractErrorMessage(errorData);

      if (isNetworkError) {
        return {
          success: false,
          error: ERROR_MESSAGES.NETWORK_ERROR,
        };
      }

      return {
        success: false,
        error: errorMessage,
      };
    }
  } catch (error) {
    if (error instanceof AxiosError) {
      const errorData = error.response?.data;
      const isNetworkError = !error.response && !!error.request;

      if (isNetworkError) {
        return {
          success: false,
          error: ERROR_MESSAGES.NETWORK_ERROR,
        };
      }

      const errorMessage = extractErrorMessage(errorData);

      return {
        success: false,
        error: errorMessage,
      };
    }

    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : ERROR_MESSAGES.UNEXPECTED_ERROR,
    };
  }
};
